import { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, KeyRound, Mail, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateEmail, validateAll } from "@/lib/validators";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const saveProfile = async () => {
    const v = validateAll([["Email", validateEmail(email)]]);
    if (!v.ok) { toast.error(v.msg); return; }

    setSavingProfile(true);
    try {
      const body = {};
      if (name && name !== user?.name) body.name = name;
      if (email && email !== user?.email) body.email = email;
      if (Object.keys(body).length === 0) {
        toast.info("Nothing to save");
        return;
      }
      await api.patch("/auth/me", body);
      toast.success("Profile updated");
      // Refresh auth context so the sidebar / topbar pick up the new name.
      if (refresh) refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (newPwd.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("New password and confirm don't match"); return; }
    if (!currentPwd) { toast.error("Enter your current password to confirm"); return; }

    setSavingPwd(true);
    try {
      await api.patch("/auth/me", { current_password: currentPwd, password: newPwd });
      toast.success("Password changed — keep it safe!");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Password change failed");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <>
      <PageHeader title="Profile & Security" subtitle="Change your name, email, and password" />
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        {/* Profile card */}
        <section className="border border-gray-200 rounded bg-white p-5 space-y-4" data-testid="card-profile">
          <header className="flex items-center gap-2 text-gray-900">
            <User className="w-4 h-4 text-blue-700" />
            <h3 className="font-semibold">Account details</h3>
          </header>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="input-profile-name" />
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Email address (used to log in)</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="input-profile-email"
            />
            <p className="text-[11px] text-gray-500">
              Changing email re-issues your login session so you stay signed in here.
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={saveProfile}
              disabled={savingProfile || (name === user?.name && email === user?.email)}
              className="bg-blue-700 hover:bg-blue-800 h-9"
              data-testid="btn-save-profile"
            >
              <Save className="w-4 h-4 mr-1.5" /> {savingProfile ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </section>

        {/* Password card */}
        <section className="border border-gray-200 rounded bg-white p-5 space-y-4" data-testid="card-password">
          <header className="flex items-center gap-2 text-gray-900">
            <KeyRound className="w-4 h-4 text-blue-700" />
            <h3 className="font-semibold">Change password</h3>
          </header>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Current password</Label>
            <Input
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              autoComplete="current-password"
              data-testid="input-current-password"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">New password</Label>
            <Input
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              autoComplete="new-password"
              data-testid="input-new-password"
            />
            <p className="text-[11px] text-gray-500">Minimum 8 characters. Use a passphrase you can remember.</p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Confirm new password</Label>
            <Input
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              autoComplete="new-password"
              data-testid="input-confirm-password"
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={savePassword}
              disabled={savingPwd || !newPwd || !confirmPwd || !currentPwd}
              className="bg-blue-700 hover:bg-blue-800 h-9"
              data-testid="btn-save-password"
            >
              <Save className="w-4 h-4 mr-1.5" /> {savingPwd ? "Saving…" : "Update password"}
            </Button>
          </div>
        </section>

        <section className="border border-gray-200 rounded bg-amber-50 p-4 text-xs text-amber-900 lg:col-span-2">
          <Mail className="w-3.5 h-3.5 inline mr-1.5" />
          Tip: keep your email up to date — it&apos;s the only way to recover your account if you forget your password.
        </section>
      </div>
    </>
  );
}
